import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { trpc } from './trpc'

export type FeedEintrag = Awaited<ReturnType<typeof trpc.tasks.aktivitaetsfeed.query>>[number]

/**
 * **Der Gelesen-Stand an einer Stelle** – „Neu für dich" und „Klopft an" teilen ihn sich
 * ([E-229](../../../../docs/ENTSCHEIDUNGEN.md#e-229)).
 *
 * Beide zeigen dieselben Kommentare, nur mit verschiedener Aufnahmeregel: der Feed alles der
 * letzten 14 Tage, „Klopft an" nur Antworten auf Laufendes und ausdrückliche Erwähnungen. Ein
 * Haken an der einen Stelle muss deshalb an der anderen wirken – zwei Kopien dieser Mutation
 * wären zwei Gelegenheiten, dass genau das nicht passiert.
 */

/**
 * **Zehn Minuten Nachlauf nach dem Abhaken** (Nils, 21.08.2026: *„dass sie nach einigen
 * Minuten dann mit dem Filter standardmäßig nicht angezeigt werden – Gelesen + 10 Minuten"*,
 * [E-203](../../../../docs/ENTSCHEIDUNGEN.md)).
 *
 * Ein Häkchen, das die Zeile im selben Moment verschwinden lässt, macht zwei Dinge falsch: es
 * verschiebt die Liste unter dem Zeiger – der nächste Klick trifft die falsche Aufgabe – und
 * es macht einen Fehlgriff unumkehrbar, weil die Zeile weg ist, bevor man merkt, dass man
 * danebengegriffen hat. Die abgehakte Zeile bleibt deshalb blass stehen, mit gesetztem Haken,
 * und geht erst danach.
 *
 * Gerechnet wird gegen den **Klickzeitpunkt vom Server** (`gelesenAm`), nicht gegen eine
 * Sitzungsuhr: sonst begänne der Nachlauf bei jedem Neuladen von vorn und vor Wochen
 * Abgehaktes stünde nach jedem F5 zehn Minuten lang wieder da.
 */
export const NACHLAUF_MS = 10 * 60_000

/** Abgehakt und die zehn Minuten sind noch nicht um. Ungelesenes ist nie im Nachlauf. */
export function imNachlauf(gelesenAm: Date | string | null | undefined, jetzt: number): boolean {
  if (!gelesenAm) return false
  return jetzt - new Date(gelesenAm).getTime() < NACHLAUF_MS
}

/**
 * **Der Takt, der den Nachlauf ablaufen lässt.**
 *
 * Ohne ihn bliebe eine abgehakte Zeile stehen, bis irgendetwas anderes ein Neuzeichnen
 * auslöst – zehn Minuten sind lang genug, dass währenddessen nichts passiert, und „blendet
 * sich gleich aus" wäre eine Zusage, die die Ansicht nicht hält.
 *
 * Der Zeitgeber läuft **nur, solange wirklich etwas im Nachlauf steht**. Ein Feed ohne frisch
 * abgehakte Zeile – der Normalfall – zeichnet nichts neu.
 *
 * **Der Rückgabewert steht zwischen zwei Schlägen still**, statt bei jedem Render ein frisches
 * `Date.now()` zu sein. Er ist eine Abhängigkeit von `useMeinTagLage` – ein bei jedem Render
 * neuer Wert machte aus dem `useMemo` dort eine Neuberechnung des ganzen Tages pro Render
 * (Leitplanke 1). Zwanzig Sekunden Ungenauigkeit sind bei zehn Minuten Nachlauf gratis.
 *
 * Ein zu alter Stand kann eine Zeile nur zu **lange** stehen lassen, nie zu kurz: wer gerade
 * abhakt, liegt selbst bei einer stundenalten Uhr innerhalb des Fensters, und der erste Schlag
 * danach zieht die Uhr nach.
 */
export function useNachlaufTakt(feed: readonly { gelesenAm?: string | Date | null }[]): number {
  const [jetzt, setJetzt] = useState(() => Date.now())
  const laeuft = feed.some((e) => imNachlauf(e.gelesenAm, jetzt))

  useEffect(() => {
    if (!laeuft) return
    const zeitgeber = setInterval(() => setJetzt(Date.now()), 20_000)
    return () => clearInterval(zeitgeber)
  }, [laeuft])

  return jetzt
}

/**
 * **Das Häkchen wirkt sofort, der Server erfährt es nebenher** (Leitplanke 1).
 *
 * Geflickt werden **alle** geladenen Feed-Abfragen (`['tasks', 'aktivitaetsfeed', …]`) – es
 * gibt drei davon: die Vorschau mit 14 Tagen und das Popup mit 14 und 30. Wer im Popup abhakt
 * und es schließt, fände die Zeile in der Vorschau sonst ungelesen wieder vor. „Klopft an"
 * rechnet aus derselben 14-Tage-Abfrage und ist damit ohne Zutun mitversorgt.
 *
 * Kein `invalidateQueries` danach: der WebSocket meldet Gelesen-Stände nicht (es ist keine
 * Änderung an der Aufgabe), und ein Nachladen des ganzen Feeds für ein Häkchen wäre der
 * teurere Weg zum selben Bild. Der Serverstand kommt beim nächsten regulären Laden an – und
 * stimmt dann mit dem überein, was hier vorhergesagt wurde.
 *
 * Bei einem Fehler wird zurückgerollt: das Häkchen springt zurück, statt eine Zeile
 * verschwinden zu lassen, die der Server nie als gelesen geführt hat.
 */
export function useGelesenSetzen() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (v: { taskId: string; bis: string; gelesen: boolean }) =>
      trpc.tasks.feedGelesen.mutate(v),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: ['tasks', 'aktivitaetsfeed'] })
      const vorher = qc.getQueriesData<FeedEintrag[]>({ queryKey: ['tasks', 'aktivitaetsfeed'] })
      const jetzt = new Date().toISOString()

      qc.setQueriesData<FeedEintrag[]>({ queryKey: ['tasks', 'aktivitaetsfeed'] }, (alt) =>
        alt?.map((e) =>
          /**
           * Dieselbe Grenze wie im Server-Verbund: gelesen ist, was **bis** zu diesem
           * Zeitpunkt geschah. Ein Ereignis, das in der Zwischenzeit hereinkam, bleibt neu.
           */
          e.taskId === v.taskId && new Date(e.am).getTime() <= new Date(v.bis).getTime()
            ? { ...e, gelesenAm: v.gelesen ? jetzt : null }
            : e,
        ),
      )
      return { vorher }
    },
    onError: (_fehler, _v, kontext) => {
      for (const [schluessel, daten] of kontext?.vorher ?? []) qc.setQueryData(schluessel, daten)
    },
  })
}
